import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { translations } from './utils/i18n';
import LanguageToggle from './components/LanguageToggle';
import RegistrationForm from './components/RegistrationForm';
import SuccessScreen from './components/SuccessScreen';
import ApartmentCarousel from './components/ApartmentCarousel';
import DirectBookingBanner from './components/DirectBookingBanner';
import BookingView from './components/BookingView';
import AmenitiesView from './components/AmenitiesView';
import CalendarView from './components/CalendarView';
import ApartmentInfo from './components/ApartmentInfo';
import SafeIcon from './common/SafeIcon';

export default function App() {
  const [lang, setLang] = useState(() => localStorage.getItem('app_lang') || 'es');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [view, setView] = useState(() => {
    const p = (window.location.pathname + window.location.hash + window.location.search).toLowerCase();
    if (p.includes('checkin') || p.includes('registro')) return 'registration';
    if (p.includes('reservas') || p.includes('disponibilidad') || p.includes('availability') || p.includes('booking')) return 'availability';
    if (p.includes('amenities') || p.includes('amenidades')) return 'amenities';
    return 'welcome';
  });
  const [initialBookingDates, setInitialBookingDates] = useState(null);
  
  const t = translations[lang];

  useEffect(() => {
    localStorage.setItem('app_lang', lang);
  }, [lang]);

  const handleBookingSubmit = async (bookingData) => {
    setIsSubmitting(true);
    try {
      const p = bookingData.pricing || {};
      await fetch('https://92afb.twidget.io/reservas', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          Checkin: bookingData.checkIn, Checkout: bookingData.checkOut, Nombre_Completo: bookingData.fullName,
          Nacionalidad: bookingData.nationality, Huspedes: String(bookingData.guests), WhatsApp: bookingData.whatsapp,
          mascotas: bookingData.pets ? 'Sí' : 'No', metododepago: bookingData.paymentType === 'card' ? 'Tarjeta' : 'Transferencia',
          valorreserva: String(p.total ?? ''), Noches: String(p.nights ?? '')
        })
      });
      try {
        const waLink = 'https://wa.me/' + String(bookingData.whatsapp || '').replace(/\D/g, '');
        const msg = ['NUEVA RESERVA DIRECTA - Apto 820', `Nombre: ${bookingData.fullName} (${bookingData.nationality})`, `Fechas: ${bookingData.checkIn} a ${bookingData.checkOut} (${p.nights ?? ''} noches)`, `Huespedes: ${bookingData.guests} | Mascotas: ${bookingData.pets ? 'Si' : 'No'}`, `Pago: ${bookingData.paymentType === 'card' ? 'Tarjeta' : 'Transferencia'} | Total: ${p.total ?? ''}`, `Escribir por WhatsApp: ${waLink}`].join('\n');
        await fetch(`https://92afb.twidget.io/notify?msg=${encodeURIComponent(msg)}`, { method: 'POST' });
      } catch (e) {}
      return true;
    } catch (err) { console.error(err); return true; } finally { setIsSubmitting(false); }
  };

  const handleFormSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
      const total = Number(formData.guests) || 1;
      const guestData = [];
      for (let i = 1; i <= total; i++) {
        const g = formData[`guest${i}`] || {};
        let photoUrl = '';
        if (g.photoFile) {
          try {
            const cd = new FormData();
            cd.append('file', g.photoFile);
            cd.append('upload_preset', 'ml_default');
            const r = await fetch('https://api.cloudinary.com/v1_1/dquqjagtw/image/upload', { method: 'POST', body: cd });
            if (r.ok) { const j = await r.json(); photoUrl = j.secure_url || ''; }
          } catch (e) {}
        }
        guestData.push({ fullName: g.fullName || '', nationality: g.nationality || '', idNumber: g.idNumber || '', photoUrl });
      }
      await fetch('https://92afb.twidget.io/820', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          checkIn: formData.checkIn, checkOut: formData.checkOut, checkInTime: formData.checkInTime,
          whatsapp: formData.whatsapp, guests: total,
          guest1: guestData[0] || {}, guest2: guestData[1] || {}, guest3: guestData[2] || {}
        })
      });
      try {
        const waLink = 'https://wa.me/' + String(formData.whatsapp || '').replace(/\D/g, '');
        const fFecha = (d) => (d ? String(d).split('-').reverse().join('/') : '');
        const lineas = guestData.filter(g => g.fullName).map((g, i) => `Huesped ${i + 1}: ${g.fullName} | ${g.nationality || '-'} | Doc: ${g.idNumber || '-'}${g.photoUrl ? ' | Foto: ' + g.photoUrl : ''}`);
        const msgHost = ['NUEVO CHECK-IN - Apto 820', `Llegada: ${formData.checkIn} ${formData.checkInTime || ''}`, `Salida: ${formData.checkOut}`, `Huespedes: ${total}`, ...lineas, `Escribir por WhatsApp: ${waLink}`].join('\n');
        await fetch(`https://92afb.twidget.io/notify?msg=${encodeURIComponent(msgHost)}`, { method: 'POST' });
        const lineasPort = guestData.filter(g => g.fullName).map(g => `- ${g.fullName}, doc ${g.idNumber || '-'}`);
        const msgPorteria = [
          'PARA PORTERIA (copiar y enviar):', 
          'Buenas, soy Javier del Apto 820. Autorizo el ingreso de:', 
          ...lineasPort, 
          `Llegada: ${fFecha(formData.checkIn)} a las ${formData.checkInTime || 'por confirmar'}.`,
          `Salida: ${fFecha(formData.checkOut)}.`,
          'Gracias.'
        ].join('\n');
        await fetch(`https://92afb.twidget.io/notify?msg=${encodeURIComponent(msgPorteria)}`, { method: 'POST' });
      } catch (e) {}
      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) { console.error(err); setIsSubmitted(true); } finally { setIsSubmitting(false); }
  };

  const getViewTitle = () => {
    if (view === 'booking') return t.bookingTitle;
    if (view === 'registration') return t.title;
    if (view === 'amenities') return t.amenitiesTitle;
    if (view === 'calendar') return t.navAvailability;
    return 'Loft Bogotá | Movistar · Simón Bolívar | Pool Rooftop';
  };

  const switchView = (v) => { 
    setView(v); 
    if (v !== 'booking') setInitialBookingDates(null); 
    window.scrollTo({ top: 0, behavior: 'smooth' }); 
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-gray-900 font-sans pb-24">
      <div className="w-full bg-emerald-900 text-emerald-50 py-3 px-4 text-center text-sm font-medium">{t.brand}</div>
      <main className="max-w-3xl mx-auto px-4 py-8">
        {!isSubmitted && <LanguageToggle lang={lang} setLang={setLang} />}
        <div className="text-center mt-8 mb-8">
          <div className="mx-auto w-16 h-16 bg-emerald-100 text-emerald-800 rounded-2xl flex items-center justify-center mb-4">
            <SafeIcon name={view === 'booking' || view === 'calendar' ? 'Calendar' : view === 'registration' ? 'UserCheck' : view === 'amenities' ? 'Zap' : 'Home'} className="text-2xl" />
          </div>
          <h1 className="text-3xl font-bold px-4">{getViewTitle()}</h1>
        </div>
        {!isSubmitted && <ApartmentCarousel />}
        {isSubmitted ? <SuccessScreen t={t} /> : (
          <div className="mt-8">
            <AnimatePresence mode="wait">
              {view === 'welcome' && (
                <motion.div key="w" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <DirectBookingBanner 
                    onRegisterClick={() => switchView('registration')} 
                    onBookingClick={() => switchView('calendar')} 
                    onAmenitiesClick={() => switchView('amenities')} 
                  />
                  <ApartmentInfo t={t} />
                </motion.div>
              )}
              {view === 'registration' && <motion.div key="r" initial={{ opacity: 0 }} animate={{ opacity: 1 }}><RegistrationForm t={t} onBack={() => switchView('welcome')} onSubmit={handleFormSubmit} isSubmitting={isSubmitting} /></motion.div>}
              {view === 'booking' && <motion.div key="b" initial={{ opacity: 0 }} animate={{ opacity: 1 }}><BookingView t={t} lang={lang} onBack={() => switchView('calendar')} onSubmit={handleBookingSubmit} isSubmitting={isSubmitting} initialDates={initialBookingDates} /></motion.div>}
              {view === 'amenities' && <motion.div key="a" initial={{ opacity: 0 }} animate={{ opacity: 1 }}><AmenitiesView t={t} onBack={() => switchView('welcome')} /></motion.div>}
              {view === 'calendar' && <motion.div key="c" initial={{ opacity: 0 }} animate={{ opacity: 1 }}><CalendarView t={t} lang={lang} onReserve={(d) => { setInitialBookingDates(d); setView('booking'); }} /></motion.div>}
            </AnimatePresence>
          </div>
        )}
      </main>
      {!isSubmitted && (
        <nav className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-6 pt-4 bg-gradient-to-t from-[#FDFBF7] to-transparent">
          <div className="max-w-md mx-auto bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl border border-emerald-100 p-2 flex items-center justify-between gap-1">
            {[
              { id: 'welcome', icon: 'Home', label: t.navHome },
              { id: 'registration', icon: 'UserCheck', label: t.navRegister },
              { id: 'calendar', icon: 'Calendar', label: t.navAvailability },
              { id: 'amenities', icon: 'Zap', label: t.navAmenities }
            ].map(item => (
              <button key={item.id} onClick={() => switchView(item.id)} className={`flex-1 flex flex-col items-center justify-center py-2 px-1 rounded-xl transition-all ${view === item.id ? 'bg-emerald-50 text-emerald-800' : 'text-gray-400'}`}>
                <SafeIcon name={item.icon} className="text-xl mb-1" />
                <span className="text-[8px] font-black uppercase tracking-tighter">{item.label}</span>
              </button>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}